import Guid from "_shared/guids/guid";
import Forum from "messaging/forums/forum";
import { User } from "users/user";
import AHDate from "_shared/dates/date";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import MessageSchema, { MessageJSON, MessageParams } from "messaging/_schemas/message-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";

export interface DraftParams {
  id: Guid;
  created: AHDate;
  forum: Forum;
  sender: User;
  content?: string;
  subject?: string;
  attachments?: File[];
}

export default class Message {
  public static readonly EXAMPLE: Message = new Message({
    id: Guid.generate(),
    senderId: Guid.generate(),
    forum: Forum.EXAMPLE,
    content: "This is a test message.",
    subject: "Test Message",
    attachments: [MessageFileInfo.EXAMPLE],
    readReceipts: [],
    issues: [],
    isAutoGenerated: false,
    created: new AHDate(),
    updated: new AHDate(),
    published: new AHDate(),
  });

  public static create(params: MessageParams): Message {
    const parsed = MessageSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Message.Create(): Invalid parameters.");
    }
    return new Message(parsed);
  }

  public static fromJSON(params: { json: MessageJSON }): Message {
    const { json } = params;
    if (isNullOrUndefined(json)) throw new Error("Message.fromJSON(): JSON object is null or undefined.");
    const messageParams = MessageSchema.deserialize(json);
    return new Message(messageParams);
  }

  public static fromJSONArray(params: { jsonArray: MessageJSON[] }): Message[] {
    const { jsonArray } = params;
    return jsonArray.map((json) =>
      Message.fromJSON({
        ...params,
        json,
      }),
    );
  }

  public static draft(params: DraftParams): Message {
    const { id, created, forum, sender, content, subject, attachments } = params;

    if (!content && (!attachments || attachments.length === 0)) throw new EmptyMessageError();
    if (!sender.id) throw new Error("Must be logged in to draft a message");

    const draftAttachments =
      attachments?.map((attachment: File) => {
        return MessageFileInfo.fromFile(attachment);
      }) ?? [];

    let newMessage = new Message({
      ...params,
      id: id,
      created: created,
      forum: forum,
      senderId: sender.id,
      content: content,
      subject: subject,
      attachments: draftAttachments,
    });

    return newMessage;
  }

  public readonly id?: Guid;
  public readonly senderId: Guid | null;
  public readonly forum: Forum;
  public readonly content?: string;
  public readonly subject?: string;
  public readonly attachments: MessageFileInfo[] = [];
  public readonly readReceipts: ReadReceipt[] = [];
  public readonly issues: string[] = [];
  public readonly isAutoGenerated?: boolean;
  public readonly created?: AHDate;
  public readonly updated?: AHDate;
  public readonly published?: AHDate;

  public constructor(params: MessageParams) {
    const validatedParams = MessageSchema.validate(params);
    this.id = validatedParams.id;
    this.senderId = validatedParams.senderId ?? null;
    this.forum = Forum.create({ ...validatedParams, ...validatedParams.forum });
    this.content = validatedParams.content;
    this.subject = validatedParams.subject;
    this.created = validatedParams.created;
    this.published = validatedParams.published;
    this.attachments =
      validatedParams.attachments?.map(
        (attachment) =>
          new MessageFileInfo({
            id: attachment.id,
            name: attachment.name,
            fileSize: attachment.fileSize,
            mimeType: attachment.mimeType,
            createdDate: attachment.createdDate,
            rawData: attachment.rawData,
            dataUri: attachment.dataUri,
          }),
      ) ?? [];
    this.issues = validatedParams.issues ?? [];
    this.readReceipts = validatedParams.readReceipts ?? [];
    this.updated = validatedParams.updated;
    this._deleted = validatedParams.deleted;
    this.isAutoGenerated = validatedParams.isAutoGenerated;
  }

  public get isDeleted(): boolean {
    return Boolean(this._deleted);
  }

  public get deleted(): AHDate | undefined {
    return this._deleted;
  }

  public get isMarkedRead(): boolean {
    return this._markedRead ?? false;
  }

  public get isMarkedUnread(): boolean {
    return this._markedUnread ?? false;
  }

  public get isMarkedForCreation(): boolean {
    return this._markedForCreation ?? false;
  }

  public get isMarkedForDeletion(): boolean {
    return this._markedForDeletion ?? false;
  }

  public get isMarkedForPublish(): boolean {
    return this._markedForPublish ?? false;
  }

  public delete(): void {
    this._deleted = new AHDate();
    this._markedForDeletion = true;
  }

  public restore(): void {
    this._deleted = undefined;
    this._markedForDeletion = false;
  }

  public markForCreation(): void {
    this._markedForCreation = true;
  }

  public markRead(): void {
    this._markedRead = true;
    this._markedUnread = false;
  }

  public markUnread(): void {
    this._markedUnread = true;
    this._markedRead = false;
  }

  public isReadByUser(userId?: Guid): boolean {
    if (!userId) return false;
    return this.readReceipts.some((receipt) => receipt.userId.isEqualTo(userId));
  }

  public userIsSender(user: User): boolean {
    return this.senderId ? this.senderId?.isEqualTo(user.id) : false;
  }

  public publish(): void {
    this._markedForPublish = true;
  }

  public addAttachments(attachments: MessageFileInfo[]): void {
    this.attachments.push(...attachments);
  }

  public toJSON(): MessageJSON {
    return MessageSchema.serialize(this);
  }

  public isEqualTo(other: Message): boolean {
    if (!other) return false;
    const idsEqual = Boolean((!this.id && !other.id) || (this.id && other.id && this.id.isEqualTo(other.id)));
    return idsEqual;
  }

  public toString(): string {
    return `Message { 
      id: ${this.id?.toString() ?? "undefined"},
      senderId: ${this.senderId?.toString() ?? "undefined"},
      forumId: ${this.forum.id?.toString() ?? "undefined"},
      subject: ${this.subject ?? "undefined"},
      content: ${this.content},
      createdDate: ${this.created?.toString() ?? "undefined"},
      updatedDate: ${this.updated?.toString() ?? "undefined"},
      publishedDate: ${this.published?.toString() ?? "undefined"},
      isDeleted: ${this.isDeleted},
      attachmentsCount: ${this.attachments.length},
      readReceiptsCount: ${this.readReceipts.length}
    }`;
  }

  private _deleted?: AHDate;
  private _markedRead?: boolean;
  private _markedUnread?: boolean;
  private _markedForCreation?: boolean;
  private _markedForDeletion?: boolean;
  private _markedForPublish?: boolean;
}

export class EmptyMessageError extends Error {
  constructor() {
    super(`Message cannot be empty`);
  }
}
